import moment from 'moment'
import { handleActions } from 'redux-actions'
import { createSelector } from 'reselect'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import * as companyModule from 'src/store/modules/company'
import * as patientModule from 'src/store/modules/patient'
import {
  activateCourse,
  deleteCourses,
  deletePatientData,
  inactivateCourse,
  setCourses,
  setIsCoursesRedirectFromDashboard,
  setRedirectedCourseUuid
} from 'src/store/modules/actionCreators'
import { patientCoursesSelector } from 'src/store/modules/patient/selectors'

import {
  COURSE_RESET_MESSAGE,
  COURSE_SAVE_MESSAGE,
  PATIENT_COURSES_PATCH,
  courseStatusesNames
} from 'src/constants'
import api from 'src/services/@core/API'
import { showSuccessModal } from 'src/services/dialogs'
import { sortByNameAsc } from 'src/utils/Object'

const initialState = {
  list: [],
  isCoursesRedirectFromDashboard: false,
  redirectedCourseUuid: ''
}

export default handleActions(
  {
    [setCourses](state, { payload }) {
      return {
        ...state,
        list: payload
      }
    },
    [deleteCourses](state, { payload }) {
      const coursesUuid = payload.map((el) => el.uuid)
      const prevCourses = state.list
      const filteredList = prevCourses.filter(
        (el) => !coursesUuid.includes(el.uuid)
      )
      return {
        ...state,
        list: filteredList
      }
    },
    [activateCourse](state, { payload }) {
      const prevCourses = state.list
      const courseToInactive = prevCourses.find((el) => el.uuid === payload)
      courseToInactive.status = 2
      const filteredList = prevCourses.filter((el) => el.uuid !== payload)
      return {
        ...state,
        list: [...filteredList, courseToInactive]
      }
    },
    [inactivateCourse](state, { payload }) {
      const prevCourses = state.list
      const courseToInactive = prevCourses.find((el) => el.uuid === payload)
      courseToInactive.status = 5
      const filteredList = prevCourses.filter((el) => el.uuid !== payload)
      return {
        ...state,
        list: [...filteredList, courseToInactive]
      }
    },
    [deletePatientData]() {
      return initialState
    },
    [setIsCoursesRedirectFromDashboard](
      state,
      { payload: isCoursesRedirectFromDashboard }
    ) {
      return {
        ...state,
        isCoursesRedirectFromDashboard
      }
    },
    [setRedirectedCourseUuid](state, { payload: redirectedCourseUuid }) {
      return {
        ...state,
        redirectedCourseUuid
      }
    }
  },
  initialState
)

/* --------- thunks --------- */
export const getPatientCourses =
  (patientUuid = '', queryParams = {}) =>
  async (dispatch) => {
    try {
      const params = { id: patientUuid }
      const query = { ...queryParams, 'per-page': 1000 }

      const { items = [] } = await api.getPatientCourses(params, query)
      dispatch(setCourses(items))
    } catch (err) {
      catchError(err)
    }
  }

export const getCourses = (filters = {}) => {
  const config = {
    query: {
      'per-page': 1000,
      ...filters.query
    }
  }
  return api.getCourses(config)
}

export const resetPatientCourse = (params) => async (dispatch) => {
  try {
    await api.resetPatientCource(params)

    dispatch(getPatientCourses(params.patientUuid))

    showSuccessModal({
      content: COURSE_RESET_MESSAGE,
      centered: true,
      keyboard: false
    })
  } catch (err) {
    catchError(err)
  }
}

export const updatePatientCourse =
  ({ params = {}, payload = {} }, patientUuid) =>
  async (dispatch) => {
    try {
      await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT_COURSES_PATCH,
          params
        },
        payload
      })

      dispatch(getPatientCourses(patientUuid))

      showSuccessModal({
        content: COURSE_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })
    } catch (err) {
      catchError(err)
    }
  }

export const deletePatientCourses = (courses) => (dispatch, getState) => {
  const id = patientModule.patientUuidSelector(getState())
  return api
    .deleteCourses({ id }, { courses })
    .then(() => dispatch(deleteCourses(courses)))
    .catch((err) => catchError(err))
}

export const activatePatientCourses = (course) => (dispatch, getState) => {
  const id = patientModule.patientUuidSelector(getState())
  return api
    .activateCourses({
      id,
      course
    })
    .then(() => dispatch(activateCourse(course)))
    .catch((err) => catchError(err))
}

export const inactivatePatientCourses = (course) => (dispatch, getState) => {
  const id = patientModule.patientUuidSelector(getState())
  const courses = { courses: [{ uuid: course }] }
  return api
    .deleteCourses({ id }, courses)
    .then(() => dispatch(inactivateCourse(course)))
    .catch((err) => catchError(err))
}

export const setPatientCourses = (courses) => (dispatch, getState) => {
  const id = patientModule.patientUuidSelector(getState())
  return api
    .postCourses({ id }, { courses })
    .then((activeCourses) => dispatch(setCourses(activeCourses.items)))
    .catch((err) => catchError(err))
}

export const coursesByDisease =
  (diseaseUUID, courses) => (dispatch, getState) => {
    const patientCourses = patientModule.patientCoursesList(getState())
    const sortByName = (a, b) => sortByNameAsc(a, b, 'name')
    const group = courses.reduce((acc, el) => {
      const isDiseaseInCourse = el.diseases.find(
        (disease) => disease.uuid === diseaseUUID
      )
      if (isDiseaseInCourse) {
        const patientCourse = patientCourses.find(
          (course) => course.uuid === el.uuid
        )
        const courseInfo = {
          uuid: el.uuid,
          name: el.name,
          status: patientCourse
            ? courseStatusesNames[patientCourse.status]
            : '',
          questionCount: el.questionCount
        }
        return [...acc, courseInfo]
      }
      return acc
    }, [])
    const sortedCourses = group.sort(sortByName)
    return sortedCourses
  }

export const groupCoursesByDisease = (courses) => (dispatch, getState) => {
  const primaryDiseaseUUID = getState().patient.profile?.diseases?.find(
    (el) => el?.type === 1
  )

  const diseases = companyModule.companyDiseasesSelector(getState())

  const sortByNameAscAndPrimaryDisease = (a, b) => {
    if (a.uuid === primaryDiseaseUUID) return -1
    if (b.uuid === primaryDiseaseUUID) return 1
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
  }
  if (primaryDiseaseUUID) {
    diseases.sort(sortByNameAscAndPrimaryDisease)
  }

  const groupedCourses = diseases.reduce((acc, disease) => {
    const group = dispatch(
      patientModule.coursesByDisease(disease.uuid, courses)
    )
    if (group.length) {
      return [
        ...acc,
        {
          ...disease,
          courses: group
        }
      ]
    }
    return acc
  }, [])
  return groupedCourses
}

export const getFilteredCourses =
  (filters = {}) =>
  (dispatch) =>
    getCourses(filters)
      .then(({ items: filteredCourses }) => {
        const groupedCourse = dispatch(
          patientModule.groupCoursesByDisease(filteredCourses)
        )
        return {
          groupedCourse,
          filteredCourses
        }
      })
      .catch((err) => {
        catchError(err)
      })

/* --------- selectors --------- */
export const patientCoursesList = (state) => {
  const selector = createSelector(
    patientCoursesSelector,
    (courses) => courses.list
  )
  return selector(state)
}

export const patientCoursesByStatusSelector = (state, status, sortBy) => {
  const getTimeDiff = (a, b) => moment(b[sortBy]).diff(moment(a[sortBy]))
  const selector = createSelector(patientCoursesList, (courses) =>
    courses
      .filter((course) => course.status === status)
      .sort((courseA, courseB) => getTimeDiff(courseA, courseB))
  )
  return selector(state)
}

export const patientRedirectedCourseUuid$ = createSelector(
  patientCoursesSelector,
  (courses) => courses.redirectedCourseUuid
)

export const isCoursesRedirectFromDashboard$ = createSelector(
  patientCoursesSelector,
  (courses) => courses.isCoursesRedirectFromDashboard
)
